import type {
	BreakpointsData,
	Component,
	ClassVariant,
	HoverVariant,
	Structure,
	PresetVariant,
	LayoutDataItems,
	SingleLayoutData,
	BreakpointRange,
} from '@wix/thunderbolt-becky-types'
import { constants } from '@wix/thunderbolt-becky-root'
import { MegaStore } from '@wix/thunderbolt-catharsis'
import type { BreakpointsToSelectorObj, ComponentCss, SelectorObj } from '../types'
import type { CompVariantsViewItem } from '../types/VariantsViewItem.types'

const { PINNED_LAYER_SUFFIX } = constants
const VARIANTS_SEPARATOR = '$$$'
export const toVariantsString = (variants: Array<string>): string => variants.join(VARIANTS_SEPARATOR)

export const isSingleLayoutData = (item: LayoutDataItems): item is SingleLayoutData => item.type === 'SingleLayoutData'

const EMPTY_ARRAY: Readonly<Array<string>> = []
export const getChildrenAndSlots = (comp: Component): Array<string> => [
	...(comp.components || EMPTY_ARRAY),
	...(comp.slots ? Object.values(comp.slots) : EMPTY_ARRAY),
]

const VARIANT_SELECTOR: Record<string, (variant: any) => string> = {
	Hover: (variant: HoverVariant) => `#${variant.componentId}:hover`,
	Class: (variant: ClassVariant) => `#${variant.componentId}.${variant.id}`,
	Preset: (variant: PresetVariant) => `.${variant.id}`,
	Mobile: () => '.device-mobile-optimized',
}

const getRegularIdSelector = (compId: string) => `#${compId}`
const getTemplateRepeaterIdSelector = (compId: string) => `[id^="${compId}__"]`
const addLayoutSelectorType = (
	compId: string,
	selector: string,
	layoutSelectorType: string = '',
	shouldOmitWrapperLayers: boolean = false
): string => {
	switch (layoutSelectorType) {
		case 'component::before':
			return `${selector}::before`
		case 'component-one-cell-grid':
			return `${selector}:not(.${compId}-container)`
		case 'component':
		case 'item':
			return selector
		default:
			if (shouldOmitWrapperLayers) {
				if (selector === getRegularIdSelector(compId)) {
					return `.${compId}-${layoutSelectorType}`
				}
			} else {
				return `${selector} .${compId}-${layoutSelectorType}`
			}
			return selector
	}
}

const getVariantSelector = (variantId: string, variants: CompVariantsViewItem) => {
	if (!variantId) {
		return ''
	}
	const variant = variants[variantId]
	return VARIANT_SELECTOR[variant.type](variant)
}

const responsiveLayoutDomSelector = (
	compId: string,
	idSelector: string,
	variantSelector: string,
	selector: string,
	shouldOmitWrapperLayers: boolean
) => {
	const hasVariant = variantSelector
	const sameComponentVariant = variantSelector && variantSelector.startsWith(idSelector)
	const compSelector = addLayoutSelectorType(
		compId,
		sameComponentVariant ? variantSelector : idSelector,
		selector,
		shouldOmitWrapperLayers
	)
	const domSelector = hasVariant && !sameComponentVariant ? `${variantSelector} ${compSelector}` : compSelector
	return domSelector
}

const pinnedLayerDomSelector = (idSelector: string) => `${idSelector}${PINNED_LAYER_SUFFIX}`

export const toMediaQuery = (item: BreakpointRange) => {
	const min = item.min ? ` and (min-width: ${item.min}px)` : ''
	const max = item.max ? ` and (max-width: ${item.max}px)` : ''
	return `@media screen${min}${max}`
}

export const selectorObjToCss = (selectorObj: SelectorObj) =>
	Object.entries(selectorObj)
		.flatMap(
			([selector, css]) =>
				`${selector}{${Object.entries(css)
					.map(([key, value]) => `${key}:${value};`)
					.join('')}}`
		)
		.join('')

export const getComponentResponsiveLayoutCss = (
	compId: string,
	isInRepeater: boolean,
	compBreakpointsOrder: BreakpointsData | undefined,
	{ responsiveLayout, pinnedLayer, layoutVariants }: ComponentCss['responsiveLayout']
): BreakpointsToSelectorObj => {
	const idSelector = isInRepeater ? getTemplateRepeaterIdSelector(compId) : getRegularIdSelector(compId)

	return ['default' as const, ...(compBreakpointsOrder?.values || [])].reduce<BreakpointsToSelectorObj>(
		(acc, breakpointRangeItem) => {
			const breakpointId = breakpointRangeItem === 'default' ? 'default' : breakpointRangeItem.id
			acc[breakpointId] = acc[breakpointId] || {}

			const responsiveLayoutInBreakpoint = responsiveLayout?.css[breakpointId]
			const pinnedLayerInBreakpoint = pinnedLayer?.[breakpointId]

			for (const variantKey in responsiveLayoutInBreakpoint) {
				const variantSelector = layoutVariants ? getVariantSelector(variantKey, layoutVariants) : ''
				const selectorObj = responsiveLayoutInBreakpoint[variantKey]
				for (const selector in selectorObj) {
					const domSelector = responsiveLayoutDomSelector(
						compId,
						idSelector,
						variantSelector,
						selector,
						!!responsiveLayout?.shouldOmitWrapperLayers
					)
					acc[breakpointId][domSelector] = acc[breakpointId][domSelector] || {}
					Object.assign(acc[breakpointId][domSelector], selectorObj[selector])
				}
			}

			for (const variantKey in pinnedLayerInBreakpoint) {
				const selectorObj = pinnedLayerInBreakpoint[variantKey]
				for (const selector in selectorObj) {
					const domSelector = pinnedLayerDomSelector(idSelector)
					acc[breakpointId][domSelector] = acc[domSelector] || {}
					Object.assign(acc[breakpointId][domSelector], selectorObj[selector])
				}
			}

			return acc
		},
		{}
	)
}

export const getComponentsResponsiveLayoutCss = (
	megaStore: MegaStore,
	structure: Structure,
	pageCompId: string
): BreakpointsToSelectorObj => {
	const cssStore = megaStore.getChildStore('componentCss')

	const result: BreakpointsToSelectorObj = { default: {} }
	const mediaQueries: { [breakpointId: string]: string } = { default: 'default' }

	const traverseComponents = (
		comp: Component,
		isInRepeater: boolean,
		ancestorComponentBreakpointsOrder: BreakpointsData | undefined
	) => {
		const compId = comp.id
		const {
			breakpointsOrder = ancestorComponentBreakpointsOrder,
			responsiveLayout,
		} = cssStore.getById<ComponentCss>(compId)

		breakpointsOrder?.values?.forEach((breakpointRangeItem) => {
			if (!mediaQueries[breakpointRangeItem.id]) {
				const mediaQuery = toMediaQuery(breakpointRangeItem)
				mediaQueries[breakpointRangeItem.id] = mediaQuery
				result[mediaQuery] = result[mediaQuery] || {}
			}
		})

		const myLayout = getComponentResponsiveLayoutCss(compId, isInRepeater, breakpointsOrder, responsiveLayout)

		for (const breakpointId in myLayout) {
			Object.assign(result[mediaQueries[breakpointId]], myLayout[breakpointId])
		}

		getChildrenAndSlots(comp).forEach((childId) =>
			traverseComponents(structure[childId], isInRepeater || comp.type === 'RepeaterContainer', breakpointsOrder)
		)
	}

	traverseComponents(structure[pageCompId], false, undefined)

	return result
}

export const serializeToString = (mediaQueriesToSelectorObj: BreakpointsToSelectorObj) => {
	return Object.entries(mediaQueriesToSelectorObj).reduce<string>((acc, [mediaQuery, breakpointCss]) => {
		const currentBreakpointCss = selectorObjToCss(breakpointCss)
		return mediaQuery === 'default'
			? `${acc}${currentBreakpointCss}`
			: `${acc}${mediaQuery}{${currentBreakpointCss}}`
	}, '')
}
